import React, { useState, useEffect } from 'react';
import {
  BuildingOffice2Icon,
  UsersIcon,
  UserPlusIcon,
  TrashIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import settingsService from '../../services/settingsService';
import authService from '../../services/auth';

// Curated set of per-member grant overrides an admin can toggle (L3).
// These override the role default for that specific member.
const GRANTABLE = [
  { feature: 'knowledge_base', action: 'edit', label: 'Edit knowledge base' },
  { feature: 'knowledge_base', action: 'delete', label: 'Delete knowledge base entries' },
  { feature: 'widgets', action: 'delete', label: 'Delete widgets' },
  { feature: 'conversations', action: 'delete', label: 'Delete conversations' },
  { feature: 'api_keys', action: 'manage', label: 'Manage API keys' },
];

const OrganizationTab = ({ onNotify }) => {
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCompany, setSavingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: '', description: '' });
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' });
  const [inviting, setInviting] = useState(false);
  const [permsOpenFor, setPermsOpenFor] = useState(null);
  const [permsDraft, setPermsDraft] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [companyData, membersData] = await Promise.all([
        settingsService.getCompanyInfo().catch(() => null),
        settingsService.getTeamMembers().catch(() => []),
      ]);
      setCompany(companyData);
      setCompanyForm({
        name: companyData?.name || '',
        description: companyData?.description || '',
      });
      setMembers(membersData);
    } catch {
      onNotify('Failed to load organization data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setSavingCompany(true);
    try {
      const updated = await settingsService.updateCompanyInfo(companyForm);
      setCompany(updated);
      onNotify('Company info updated successfully');
    } catch (err) {
      onNotify(err.response?.data?.detail || 'Failed to update company', 'error');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await settingsService.inviteTeamMember(inviteForm.email, inviteForm.role);
      setShowInvite(false);
      setInviteForm({ email: '', role: 'member' });
      onNotify('Team member added successfully');
      loadData();
    } catch (err) {
      onNotify(err.response?.data?.detail || 'Failed to add team member', 'error');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await settingsService.updateMemberRole(userId, newRole);
      onNotify('Member role updated');
      loadData();
    } catch (err) {
      onNotify(err.response?.data?.detail || 'Failed to update role', 'error');
    }
  };

  const openPerms = (member) => {
    if (permsOpenFor === member.user_id) {
      setPermsOpenFor(null);
      return;
    }
    setPermsOpenFor(member.user_id);
    setPermsDraft(member.permissions || {});
  };

  const isGranted = (feature, action) => !!permsDraft?.[feature]?.[action];

  const toggleGrant = (feature, action) => {
    setPermsDraft((prev) => {
      const next = { ...prev, [feature]: { ...(prev[feature] || {}) } };
      next[feature][action] = !next[feature][action];
      return next;
    });
  };

  const handleSavePerms = async (userId) => {
    setSavingPerms(true);
    try {
      await settingsService.updateMemberPermissions(userId, permsDraft);
      onNotify('Member permissions updated');
      setPermsOpenFor(null);
      loadData();
    } catch (err) {
      onNotify(err.response?.data?.detail || 'Failed to update permissions', 'error');
    } finally {
      setSavingPerms(false);
    }
  };

  const handleRemoveMember = async (userId, username) => {
    if (!window.confirm(`Remove ${username} from the company?`)) return;
    try {
      await settingsService.removeTeamMember(userId);
      onNotify('Member removed');
      loadData();
    } catch (err) {
      onNotify(err.response?.data?.detail || 'Failed to remove member', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
        <BuildingOffice2Icon className="h-12 w-12 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400">No company associated with your account.</p>
        <p className="text-gray-500 text-sm mt-1">Update your company name in Profile to create a company.</p>
      </div>
    );
  }

  const roleBadge = (role) => {
    const styles = {
      owner: 'bg-purple-900/30 text-purple-400 border-purple-800',
      admin: 'bg-blue-900/30 text-blue-400 border-blue-800',
      member: 'bg-gray-700 text-gray-400 border-gray-600',
    };
    return styles[role] || styles.member;
  };

  return (
    <div className="space-y-6">
      {/* Company Info */}
      <form onSubmit={handleSaveCompany} className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <BuildingOffice2Icon className="h-6 w-6 text-blue-400" aria-hidden="true" />
          <h3 className="text-lg font-medium text-white">Company Information</h3>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="org-company-name" className="block text-sm font-medium text-gray-300 mb-2">Company Name</label>
              <input
                id="org-company-name"
                name="company_name"
                type="text"
                autoComplete="organization"
                value={companyForm.name}
                onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="org-slug" className="block text-sm font-medium text-gray-300 mb-2">Slug</label>
              <input
                id="org-slug"
                name="slug"
                type="text"
                value={company.slug}
                disabled
                className="w-full bg-gray-700/50 border border-gray-600 rounded-lg px-3 py-2 text-gray-500"
              />
            </div>
          </div>
          <div>
            <label htmlFor="org-description" className="block text-sm font-medium text-gray-300 mb-2">Description</label>
            <textarea
              id="org-description"
              name="description"
              value={companyForm.description}
              onChange={(e) => setCompanyForm({ ...companyForm, description: e.target.value })}
              rows={2}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Brief description of your company"
            />
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>Created: {new Date(company.created_at).toLocaleDateString()}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${
              company.is_active ? 'bg-green-900/30 text-green-400 border border-green-800' : 'bg-red-900/30 text-red-400 border border-red-800'
            }`}>
              {company.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={savingCompany}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg transition-colors"
            >
              {savingCompany ? 'Saving…' : 'Save Company Info'}
            </button>
          </div>
        </div>
      </form>

      {/* Team Members */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <UsersIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />
            <h3 className="text-lg font-medium text-white">Team Members</h3>
            <span className="text-sm text-gray-500">({members.length})</span>
          </div>
          <button
            type="button"
            onClick={() => setShowInvite(!showInvite)}
            aria-expanded={showInvite}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors motion-reduce:transition-none"
          >
            <UserPlusIcon className="h-4 w-4" aria-hidden="true" />
            Add Member
          </button>
        </div>

        {/* Invite Form */}
        {showInvite && (
          <form onSubmit={handleInvite} className="bg-gray-700/50 rounded-lg p-4 mb-4">
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label htmlFor="invite-email" className="block text-sm font-medium text-gray-300 mb-1">Email Address</label>
                <input
                  id="invite-email"
                  name="invite-email"
                  type="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  required
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label htmlFor="invite-role" className="block text-sm font-medium text-gray-300 mb-1">Role</label>
                <select
                  id="invite-role"
                  name="invite-role"
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                >
                  <option value="viewer">Viewer</option>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={inviting}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors motion-reduce:transition-none"
              >
                {inviting ? 'Adding…' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">User must already have an account with that email.</p>
          </form>
        )}

        {/* Members List */}
        <div className="space-y-2">
          {members.map((member) => (
            <div key={member.user_id} className="bg-gray-700/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-sm font-bold text-white">
                    {member.username.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{member.username}</p>
                    <p className="text-xs text-gray-500">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={member.role}
                    onChange={(e) => handleUpdateRole(member.user_id, e.target.value)}
                    disabled={member.role === 'owner'}
                    aria-label={`Role for ${member.username}`}
                    className={`text-xs rounded-full px-3 py-1 border ${roleBadge(member.role)} bg-transparent disabled:cursor-not-allowed`}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="owner">Owner</option>
                  </select>
                  {member.role !== 'owner' && (
                    <button
                      type="button"
                      onClick={() => openPerms(member)}
                      aria-expanded={permsOpenFor === member.user_id}
                      className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                      title="Edit per-member permissions"
                    >
                      <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" />
                      Permissions
                    </button>
                  )}
                  {member.role !== 'owner' && (
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(member.user_id, member.username)}
                      className="text-red-400 hover:text-red-300 p-1"
                      aria-label={`Remove ${member.username}`}
                      title="Remove member"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>

              {/* Per-member permission overrides */}
              {permsOpenFor === member.user_id && (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <p className="text-xs text-gray-400 mb-2">
                    Grant overrides for {member.username} — checked = allowed, overriding their role default.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {GRANTABLE.map((g) => (
                      <label
                        key={`${g.feature}.${g.action}`}
                        className="flex items-center gap-2 text-sm text-gray-300 bg-gray-800/60 rounded px-2 py-1.5 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isGranted(g.feature, g.action)}
                          onChange={() => toggleGrant(g.feature, g.action)}
                          className="rounded border-gray-500 bg-gray-700 text-blue-500 focus:ring-blue-500"
                        />
                        {g.label}
                      </label>
                    ))}
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => setPermsOpenFor(null)}
                      className="text-xs px-3 py-1.5 rounded bg-gray-600 hover:bg-gray-500 text-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSavePerms(member.user_id)}
                      disabled={savingPerms}
                      className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
                    >
                      {savingPerms ? 'Saving…' : 'Save permissions'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {members.length === 0 && (
            <p className="text-gray-500 text-sm py-4 text-center">No team members found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationTab;
